import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { ShoppingCart, User, Menu, X, Leaf, Search, Filter, Star, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import WishlistButton from "../components/WishlistButton";
import Navbar from "../components/Navbar";

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");
  const [ecoOnly, setEcoOnly] = useState(searchParams.get("eco_only") === "true");
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "furniture", label: "Furniture" },
    { value: "interior-decor", label: "Interior Decor" },
    { value: "eco-sustainable", label: "Eco Sustainable" },
    { value: "handmade-crafts", label: "Handmade Crafts" },
    { value: "artwork", label: "Artwork" },
    { value: "finishing-materials", label: "Finishing Materials" },
    { value: "building-materials", label: "Building Materials" }
  ];

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, ecoOnly, priceRange]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (searchTerm) params.append("search", searchTerm);
      if (ecoOnly) params.append("eco_only", "true");
      if (priceRange[0] > 0) params.append("min_price", priceRange[0]);
      if (priceRange[1] < 2000) params.append("max_price", priceRange[1]);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A]">
            {selectedCategory !== "all" ? categories.find(c => c.value === selectedCategory)?.label : "All Products"}
          </h1>
          <p className="text-[#5C5D5C] mt-2 font-light">
            {products.length} products found
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8F8E]" size={20} />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-[#EAE6DF] rounded-none h-12"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none h-12 px-6" data-testid="search-btn">
              Search
            </Button>
          </form>

          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="border-[#EAE6DF] rounded-none h-12"
            data-testid="filter-toggle"
          >
            <Filter size={18} className="mr-2" /> Filters
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-[#EAE6DF] p-6 mb-8 grid md:grid-cols-4 gap-6" data-testid="filters-panel">
            <div>
              <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="rounded-none border-[#EAE6DF]" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Price Range</label>
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={2000}
                  step={50}
                  className="my-4"
                  data-testid="price-slider"
                />
                <div className="flex justify-between text-sm text-[#5C5D5C]">
                  <span>£{priceRange[0]}</span>
                  <span>£{priceRange[1]}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="eco" 
                  checked={ecoOnly} 
                  onCheckedChange={setEcoOnly}
                  data-testid="eco-checkbox"
                />
                <label htmlFor="eco" className="text-sm font-medium text-[#1A1B1A] cursor-pointer flex items-center gap-2">
                  <Leaf size={16} className="text-[#3E6656]" /> Eco Sustainable Only
                </label>
              </div>
            </div>

            <div className="flex items-center">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSelectedCategory("all");
                  setEcoOnly(false);
                  setPriceRange([0, 2000]);
                  setSearchTerm("");
                }}
                className="text-[#F06570] hover:text-[#D9525D]"
                data-testid="clear-filters"
              >
                Clear All Filters
              </Button>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#5C5D5C] text-lg">No products found matching your criteria.</p>
            <Button 
              onClick={() => {
                setSelectedCategory("all");
                setEcoOnly(false);
                setPriceRange([0, 2000]);
                setSearchTerm("");
              }}
              className="mt-4 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="product-card bg-white overflow-hidden cursor-pointer relative"
                onClick={() => navigate(`/products/${product.id}`)}
                data-testid={`product-item-${index}`}
              >
                <WishlistButton productId={product.id} className="absolute top-3 right-3 z-10" />
                <div className="aspect-square overflow-hidden bg-[#EFECE5]">
                  <img 
                    src={product.images?.[0] || "https://via.placeholder.com/400"} 
                    alt={product.name}
                    className="w-full h-full object-cover product-image"
                  />
                </div>
                <div className="p-4">
                  {product.eco_sustainable && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#3E6656] bg-[#3E6656]/10 px-2 py-1 rounded mb-2">
                      <Leaf size={12} /> Eco
                    </span>
                  )}
                  <h3 className="font-medium text-[#1A1B1A] mt-1 line-clamp-1">{product.name}</h3>
                  <p className="text-sm text-[#5C5D5C] mt-1 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-lg font-semibold text-[#1A1B1A]">£{product.price}</span>
                    {product.rating > 0 && (
                      <span className="flex items-center gap-1 text-sm text-[#5C5D5C]">
                        <Star size={14} className="fill-[#D4A373] text-[#D4A373]" />
                        {product.rating}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
